import React, { useCallback, useMemo, useState } from 'react';
import _ from 'lodash';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

import SimpleTable, { createDefaultActions, SortDirection } from 'components/Common/SimpleTable/SimpleTable';
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal';
import ContentPage from 'components/ContentView/ContentPage/ContentPage';

import { useTranslation } from 'localization/useTranslation';
import useApiCall from 'services/useApiCall';
import useAPI from 'services/useAPI';

import useAlerts from 'utils/hooks/useAlerts';
import Tl from 'localization/LocalizationKeys';

import { useUsersQuery } from './queries';

const columns = [
  {
    label: Tl.admin.users.column.login,
    path: 'email',
    searchItem: true,
    isSortable: true
  },
  {
    label: Tl.common.name,
    path: 'displayName',
    isSortable: true
  }
];

const DefaultSortBy = {
  path: 'name',
  direction: SortDirection.DESC
};


const AdminUserPageList = ({ breadcrumbs }) => {
  const T = useTranslation();
  const [deleteUser, setDeleteUser] = useState(null);
  const api = useAPI();
  const history = useHistory();

  const [alerts, addSuccessAlert, addErrorAlert] = useAlerts();
  const usersQuery = useUsersQuery();
  const isLoadingUsers = usersQuery.isLoading;
  const users = usersQuery.data;
  const hasError = usersQuery.isError;
  const refetch = usersQuery.refetch;

  const [deleteUserApi, isDeletingUser] = useApiCall({
    apiCall: api.users.deleteUser,
    onSuccess: useCallback(() => {
      addSuccessAlert(T.admin.deleteuser.success);
      setDeleteUser(null);
      refetch();
    }, [T.admin.deleteuser.success, addSuccessAlert, refetch]),
    onError: useCallback((error) => {
      console.error('Failed to Delete User', error);
      addErrorAlert(T.admin.deleteuser.error);
    }, [T.admin.deleteuser.error, addErrorAlert])
  });


  const confirmedDelete = useCallback((confirmedDeleteUser) => {
    deleteUserApi(confirmedDeleteUser.id);
  }, [deleteUserApi]);

  const onDelete = useCallback(user => setDeleteUser(user), []);
  const cancelDelete = useCallback(() => setDeleteUser(null), []);

  const onCreateUser = useCallback(() => {
    history.push('/admin/users/new-user');
  }, [history]);

  const onRowClick = useCallback((user) => {
    history.push(`/admin/users/${user.id}`);
  }, [history]);


  const actions = useMemo(() => [
    ...createDefaultActions({ onDelete, onEdit: onRowClick })
  ], [onDelete, onRowClick]);

  return (
    <ContentPage breadcrumb={breadcrumbs} help={'admin/users/list'}>
      { alerts }
      <SimpleTable
        actions={actions}
        isLoading={isLoadingUsers && _.isEmpty(users)}
        onRowClick={onRowClick}
        data={users}
        columns={columns}
        globalActions={[{ onClick: onCreateUser }]}
        sortBy={DefaultSortBy}
        hasError={hasError}
        errorText={T.admin.user.list.error}
        compact
        usePaging
      />
      <ConfirmDeleteModal
        isLoading={isDeletingUser}
        onConfirm={confirmedDelete}
        item={deleteUser}
        itemName={deleteUser?.displayName ?? T.common.noname}
        onCancel={cancelDelete}
      />
    </ContentPage>
  );
};

export default AdminUserPageList;
